import { Box } from "@biom3/react";
import type { Content } from "@prismicio/client";

import { useOnboardingQuestData } from "@hooks/quest/useOnboardingQuestData";
import type { WishlistProps } from "@ui-kit";
import {
  type QuestGroup,
  QuestGroups,
  type QuestKey,
  type QuestState,
} from "@ui-kit/QuestList";

import { useAccessToken } from "@/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SOCIAL_LINK_ERROR_MESSAGES } from "@/constants";
import { usePassportProvider } from "@/context";
import { useQuest } from "@/context/QuestProvider";
import { UNLOCK_QUEST, UPDATE_QUEST } from "@/context/QuestProvider/actions";
import { useGetWalletGems, useGetWalletLatestEarnings } from "@/hooks/api";
import { useCompleteQuest } from "@/hooks/quest/useCompleteQuest";
import { useOnboardingQuests } from "@/hooks/quest/useOnboardingQuests";
import { useResetQuest } from "@/hooks/quest/useResetQuest";
import { useSkipQuest } from "@/hooks/quest/useSkipQuest";
import { useSubmitQuest } from "@/hooks/quest/useSubmitQuest";
import {
  linkedAccountPlatforms,
  openAuthorizeUrl,
  validateQuest,
} from "@/utils/linkedaccounts/linkedAccounts";
import { trackLegacyEvent } from "@analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStoreImageValidationModal } from "./AppStoreImageValidationModal";
import { FollowConfirmationModal } from "./FollowConfirmationModal";
import { QuestModalsControls } from "./QuestModalsControls";
import { XActionConfirmationModal } from "./XActionConfirmationModal";
import { LikeRetweetXModal } from "./v2/LikeRetweetXModal";
import { PostTweetXModal } from "./v2/PostTweetXModal";

type QuestsProps = {
  show: boolean;
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
  useVariantBExperiment?: boolean;
  questGroup?: string;
  questsGroups?: QuestGroup[] | null;
};

export const Quests = (props: QuestsProps) => {
  const { userInfo, walletAddress } = usePassportProvider();
  const { latestEarnings } = useGetWalletLatestEarnings(walletAddress);
  const { walletGems, mutate: mutateGetWalletGems } = useGetWalletGems(
    walletAddress || null,
    false,
  );
  const { xQuestDisabled } = useFlags<{
    xQuestDisabled: boolean | undefined;
  }>();

  const {
    show,
    game,
    wishlistProps: { onWishlistClick, isWishlisted },
    useVariantBExperiment,
    questGroup,
    questsGroups,
  } = props;

  const {
    questsGroups: staticQuestGroups,
    hasOnboardingQuests: hasOnboardingQuestsFromData,
  } = useOnboardingQuestData(game.uid, game.uid === "norma-in-metaland");

  const hasCMSQuests = !!(questsGroups && questsGroups.length > 0);
  const hasOnboardingQuests = hasCMSQuests ? true : hasOnboardingQuestsFromData;
  const quests = hasCMSQuests ? questsGroups : staticQuestGroups;

  const [selectedQuest, setSelectedQuest] = useState<QuestState | undefined>(
    undefined,
  );
  const asyncCompleteHandler = useRef<
    (() => Promise<Error | undefined>) | undefined
  >(undefined);

  const revalidateGemsAmount = useCallback(
    () =>
      walletGems &&
      mutateGetWalletGems({ ...walletGems }, { revalidate: true }),
    [walletGems, mutateGetWalletGems],
  );

  const gameId = game.data.games_service_item?.id;

  // calling to initialise the quests
  useOnboardingQuests(game, quests, isWishlisted);

  const { dispatchAction, state: questGroupState } = useQuest((store) => store);

  const [showXActionConfirmationModal, setShowXActionConfirmationModal] =
    useState(false);
  const [showLikeRetweetXModal, setShowLikeRetweetXModal] = useState(false);
  const [showPostTweetXModal, setShowPostTweetXModal] = useState(false);
  const [
    showAppStoreImageValidationModal,
    setShowAppStoreImageValidationModal,
  ] = useState(false);

  const { completeQuest } = useCompleteQuest(
    revalidateGemsAmount,
    setSelectedQuest,
    asyncCompleteHandler,
  );
  const { resetQuest } = useResetQuest(
    revalidateGemsAmount,
    asyncCompleteHandler,
    setSelectedQuest,
  );
  const { submitQuest } = useSubmitQuest(
    setSelectedQuest,
    onWishlistClick,
    asyncCompleteHandler,
  );
  const { skipQuest } = useSkipQuest(walletAddress, game, latestEarnings);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === "social_auth_error" &&
        event.newValue &&
        showConfirmation
      ) {
        setError(event.newValue);
        setShowConfirmation(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [showConfirmation]);

  const withQuests = useMemo(
    () => show && hasOnboardingQuests && quests.length > 0,
    [show, hasOnboardingQuests, quests],
  );

  const getAccessToken = useAccessToken();

  const totalGemsGranted = useMemo(
    () =>
      questGroupState
        .filter((group) =>
          group.quests
            .filter((quest: QuestState) => quest.key !== "bonus")
            .every((quest: QuestState) => quest.completed),
        )
        .reduce((acc, group) => acc + group.totalGemsGranted, 0),
    [questGroupState],
  );

  const allGroupsCompleted = useMemo(
    () =>
      questGroupState
        .filter((g) => !g.showOnAllCompleted)
        .every((g) => g.quests.every((q: QuestState) => q.completed)),
    [questGroupState],
  );

  const filteredQuestState = useMemo(() => {
    if (!questGroup) return questGroupState;
    return questGroupState.filter((group) => group.questGroup === questGroup);
  }, [questGroupState, questGroup]);

  const onQuestClick = useCallback(
    (quest: QuestState) => {
      if (
        quest.key === "x-like-and-retweet-validation" &&
        quest?.likeRetweetId
      ) {
        setSelectedQuest(quest);
        setShowLikeRetweetXModal(true);
        return;
      }

      if (
        quest.key === "x-post-tweet-validation" ||
        quest.key === "x-like-and-retweet-validation"
      ) {
        setSelectedQuest(quest);
        setShowXActionConfirmationModal(true);
        //setShowPostTweetXModal(true);
        return;
      }

      if (quest.key === "app-store-image-validation") {
        setSelectedQuest(quest);
        setShowAppStoreImageValidationModal(true);
        return;
      }

      if (linkedAccountPlatforms.get(quest.key)) {
        localStorage.removeItem(quest.ruleId);
        setShowConfirmation(true);
        if (quest.key === "x-v2" && xQuestDisabled) {
          setSelectedQuest(quest);
          setError(SOCIAL_LINK_ERROR_MESSAGES.X_FOLLOW_QUEST_UNAVAILABLE);
        } else {
          const twitterHandle = quest.label.match(/@(\w+)/)?.[1];
          openAuthorizeUrl(
            quest.ruleId,
            quest.key,
            quest.linkUrl || "",
            game.data.name ?? "",
            gameId as string,
            undefined, // loginOnly
            undefined, // tweetText
            undefined, // twitterHandle
            undefined, // tweetImageId
            quest.linkUrl, // linkUrl
          );
          submitQuest(quest);
        }
      } else {
        submitQuest(quest);
      }
    },
    [submitQuest, game, gameId, xQuestDisabled],
  );

  const onQuestClickHandler = useCallback(
    (quest: QuestState) => {
      if (quest.skippable) {
        submitQuest(quest);
        return;
      }
      onQuestClick(quest);
    },
    [onQuestClick, submitQuest],
  );

  const onQuestLockTimerExpired = useCallback(
    (quest: QuestState) => {
      dispatchAction({
        type: UNLOCK_QUEST,
        payload: { id: quest.ruleId },
      });
      dispatchAction({
        type: UPDATE_QUEST,
        payload: {
          id: quest.ruleId,
          quest: { icon: quest.baseIcon },
        },
      });
    },
    [dispatchAction],
  );

  const handleSelectedQuestClick = useCallback(() => {
    if (selectedQuest) {
      onQuestClick(selectedQuest);
    }
  }, [selectedQuest, onQuestClick]);

  const onConfirmFollow = useCallback(async () => {
    try {
      // If they failed to complete the quest make them complete the flow again so they can't spam the button and reach the rate limit
      if (error) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      const quest = selectedQuest;
      if (!quest) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      // If the quest was completed in the login screen show the "success" state
      if (localStorage.getItem(quest.ruleId) === "true") {
        setShowConfirmation(false);
        await completeQuest(selectedQuest);
        setError(null);
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }

      if (!gameId) {
        return;
      }

      // start time is currently only set for x-share-validation quests (should be RFC3339 format)
      const startTime =
        quest.key === "x-share-validation" && quest.startTime
          ? quest.startTime.toISOString().replace(".000Z", "Z")
          : "";

      const following = await validateQuest(
        gameId as string,
        quest.key,
        quest.ruleId,
        accessToken,
        startTime,
      );
      if (!following) {
        throw new Error("the quest is not complete");
      }
      trackLegacyEvent({
        screen: "DetailsQuests",
        userJourney: "Games",
        action: "Succeeded",
        control: "SocialQuest",
        controlType: "Effect",
        extras: {
          questId: quest.ruleId || "",
          platform: quest.key || "",
        },
      });

      setShowConfirmation(false);
      setError(null);
      await completeQuest(selectedQuest);
    } catch (err) {
      trackLegacyEvent({
        screen: "DetailsQuests",
        userJourney: "Games",
        control: "Failed",
        controlType: "Effect",
        extras: {
          questId: selectedQuest?.ruleId || "",
          platform: selectedQuest?.key || "",
        },
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while confirming follow.";

      setError(errorMessage);
    }
  }, [selectedQuest, error, getAccessToken, completeQuest, gameId]);

  const onRetry = useCallback(() => {
    if (selectedQuest) {
      setError(null);
      localStorage.removeItem("social_auth_error");
      window.open(selectedQuest.linkUrl || "", "_blank");
    }
  }, [selectedQuest]);

  if (!withQuests) {
    return <Box sx={{ pb: "base.spacing.x3" }} />;
  }

  return (
    <>
      <QuestGroups
        game={game}
        groups={filteredQuestState}
        onQuestClick={onQuestClickHandler}
        onQuestLockTimerExpired={onQuestLockTimerExpired}
        isWishlisted={isWishlisted}
        allGroupsCompleted={allGroupsCompleted}
        userId={userInfo?.sub || "anonymous"}
        useVariantBExperiment={useVariantBExperiment}
      />
      <QuestModalsControls
        game={game}
        quest={selectedQuest}
        onModalClose={resetQuest}
        onQuestComplete={() => selectedQuest && completeQuest(selectedQuest)}
        totalGemsGranted={totalGemsGranted}
      />
      <FollowConfirmationModal
        visible={showConfirmation}
        onConfirm={onConfirmFollow}
        onClose={() => {
          setShowConfirmation(false);
          setError(null);
          resetQuest(true);
          localStorage.removeItem("social_auth_error");
        }}
        onRetry={onRetry}
        onQuestClick={handleSelectedQuestClick}
        onSkip={() => selectedQuest && skipQuest(selectedQuest)}
        questKey={selectedQuest?.key}
        linkUrl={selectedQuest?.linkUrl}
        ruleId={selectedQuest?.ruleId}
        gameName={game?.data?.name || "the game"}
        error={error}
        setError={setError}
      />
      <XActionConfirmationModal
        visible={showXActionConfirmationModal}
        onClose={() => setShowXActionConfirmationModal(false)}
        quest={selectedQuest}
        game={game}
        showConfirmationModal={() => setShowConfirmation(true)}
      />
      <LikeRetweetXModal
        visible={showLikeRetweetXModal}
        onClose={() => setShowLikeRetweetXModal(false)}
        questRuleId={selectedQuest?.ruleId || ""}
        questKey={(selectedQuest?.key as QuestKey) || ""}
        questLink={selectedQuest?.linkUrl || ""}
        gameId={game.data.games_service_item?.id as string}
        gameName={game.data.name as string}
        showConfirmationModal={() => setShowConfirmation(true)}
        tweetId={selectedQuest?.likeRetweetId || ""}
      />
      <PostTweetXModal
        visible={showPostTweetXModal}
        onClose={() => setShowPostTweetXModal(false)}
        questDetails={{
          questRuleId: selectedQuest?.ruleId || "",
          questKey: selectedQuest?.key as QuestKey,
          questLink: selectedQuest?.linkUrl || "",
        }}
        tweetDetails={{
          tweetText: selectedQuest?.tweetText,
          tweetUsername: selectedQuest?.tweetUsername,
          tweetRequiresImage: selectedQuest?.tweetRequiresImage,
        }}
        completionDetails={{
          completed: selectedQuest?.completed,
          latestCompletion: selectedQuest?.completedAt,
          refreshPeriodHours: selectedQuest?.recurringRefreshPeriodDays
            ? selectedQuest?.recurringRefreshPeriodDays * 24
            : undefined,
          validFrom: selectedQuest?.startTime,
        }}
        gameId={game.data.games_service_item?.id as string}
        gameName={game.data.name as string}
        gameUid={game?.uid || ""}
        showConfirmationModal={() => setShowConfirmation(true)}
      />
      <AppStoreImageValidationModal
        visible={showAppStoreImageValidationModal}
        onClose={() => setShowAppStoreImageValidationModal(false)}
        validationType={selectedQuest?.actionValidationType}
        questId={selectedQuest?.ruleId || ""}
        gameUid={game?.uid || ""}
        gameId={game.data.games_service_item?.id as string}
      />
    </>
  );
};
